import {
  CondicaoFormulario,
  EtapaFormulario,
  ItemFormulario,
  SchemaFormulario,
  TipoCampoFormulario,
} from '@/types/formularioAdmin'

/**
 * Regras do editor de formulários do produto (AN-6 §1.1). **Quem recusa é o backend**
 * (`ValidadorPublicacao`, no PUT do rascunho e na publicação); aqui a tela só avisa antes,
 * com as mesmas regras, para o ADMIN não descobrir o problema no clique de publicar.
 */

export const TIPOS_NUMERICOS: TipoCampoFormulario[] = ['NUMERO', 'MOEDA', 'PERCENTUAL']

export const ehNumerico = (tipo: TipoCampoFormulario) => TIPOS_NUMERICOS.includes(tipo)

/** Chave estável a partir do rótulo: snake_case ASCII, até 60 caracteres (coluna da V55). */
export const chaveDoRotulo = (rotulo: string): string =>
  rotulo
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)

export const novoItem = (tipo: TipoCampoFormulario = 'TEXTO'): ItemFormulario =>
  tipo === 'GRUPO'
    ? { chave: '', rotulo: '', tipo, repeatableGroup: true, campos: [] }
    : { chave: '', rotulo: '', tipo }

/**
 * Campos que um item pode referenciar numa condição: os anteriores do topo e, dentro de um
 * grupo, os irmãos anteriores. Grupos não são referenciáveis (não têm valor próprio).
 */
export const camposReferenciaveis = (
  itensTopo: ItemFormulario[],
  indiceTopo: number,
  indiceNoGrupo?: number
): ItemFormulario[] => {
  const anterioresTopo = itensTopo.slice(0, indiceTopo).filter((i) => i.tipo !== 'GRUPO')
  if (indiceNoGrupo === undefined) return anterioresTopo
  const irmaos = (itensTopo[indiceTopo]?.campos ?? []).slice(0, indiceNoGrupo)
  return [...anterioresTopo, ...irmaos]
}

const condicaoProblema = (
  c: CondicaoFormulario | null | undefined,
  referenciaveis: ItemFormulario[],
  onde: string,
  nome: string
): string | null => {
  if (!c) return null
  if (!c.campo) return `${onde}: escolha o campo da ${nome}.`
  if (!referenciaveis.some((r) => r.chave === c.campo)) {
    return `${onde}: a ${nome} usa "${c.campo}", que não existe antes deste campo.`
  }
  if (c.operador === 'UM_DE') {
    if (valoresLimpos(c).length === 0) return `${onde}: informe ao menos um valor da ${nome}.`
  } else if (c.operador !== 'PREENCHIDO' && !(c.valor ?? '').trim()) {
    return `${onde}: informe o valor da ${nome}.`
  }
  return null
}

/** Valores de UM_DE sem espaços nas pontas e sem vazios. */
const valoresLimpos = (c: CondicaoFormulario): string[] =>
  (c.valores ?? []).map((v) => v.trim()).filter((v) => v !== '')

/**
 * Forma canônica da condição (contrato do backend, `CondicaoFormulario.java`): UM_DE →
 * `valores: string[]`; PREENCHIDO → sem valor; demais → `valor` (texto).
 */
export const normalizarCondicao = (c: CondicaoFormulario | null | undefined): CondicaoFormulario | undefined => {
  if (!c) return undefined
  if (c.operador === 'UM_DE') return { campo: c.campo, operador: c.operador, valores: valoresLimpos(c) }
  if (c.operador === 'PREENCHIDO') return { campo: c.campo, operador: c.operador }
  return { campo: c.campo, operador: c.operador, valor: c.valor ?? '' }
}

const problemasDoCampo = (
  item: ItemFormulario,
  etapa: EtapaFormulario,
  onde: string,
  referenciaveis: ItemFormulario[]
): string[] => {
  const p: string[] = []
  if (!item.rotulo.trim()) p.push(`${onde}: falta o rótulo.`)
  if (!item.chave.trim()) p.push(`${onde}: falta a chave.`)
  if (item.tipo === 'OPCAO') {
    const opcoes = item.opcoes ?? []
    if (opcoes.length === 0) p.push(`${onde}: campo de opções sem nenhuma opção.`)
    if (opcoes.some((o) => !o.valor.trim() || !o.rotulo.trim())) p.push(`${onde}: há opção sem valor ou rótulo.`)
  }
  if (item.min != null && item.max != null && item.min > item.max) {
    p.push(`${onde}: o mínimo é maior que o máximo.`)
  }
  if (item.tipo === 'CPF' && item.enviarParaIa) p.push(`${onde}: CPF nunca é enviado à IA.`)
  if (etapa === 'ESTRUTURACAO' && item.obrigatorioEm === 'ENVIO_ANALISE') {
    p.push(`${onde}: na Estruturação a reserva já foi enviada — use "Encaminhamento ao parceiro".`)
  }
  const c1 = condicaoProblema(item.condition, referenciaveis, onde, 'condição de exibição')
  if (c1) p.push(c1)
  const c2 = condicaoProblema(item.requiredWhen, referenciaveis, onde, 'condição de obrigatoriedade')
  if (c2) p.push(c2)
  return p
}

/** Lista legível do que impede salvar/publicar. Vazia = a tela libera os botões. */
export const problemasDoSchema = (schema: SchemaFormulario, etapa: EtapaFormulario): string[] => {
  const problemas: string[] = []
  const chavesTopo = new Map<string, number>()
  schema.itens.forEach((item, i) => {
    const onde = `Campo ${i + 1}${item.rotulo ? ` ("${item.rotulo}")` : ''}`
    if (item.chave) chavesTopo.set(item.chave, (chavesTopo.get(item.chave) ?? 0) + 1)
    problemas.push(...problemasDoCampo(item, etapa, onde, camposReferenciaveis(schema.itens, i)))

    if (item.tipo !== 'GRUPO') return
    const campos = item.campos ?? []
    if (campos.length === 0) problemas.push(`${onde}: grupo sem campos.`)
    if (item.minItems != null && item.maxItems != null && item.minItems > item.maxItems) {
      problemas.push(`${onde}: mínimo de itens maior que o máximo.`)
    }
    if (item.quantidadeDe) {
      const alvo = schema.itens.slice(0, i).find((x) => x.chave === item.quantidadeDe)
      if (!alvo) problemas.push(`${onde}: a quantidade vem de "${item.quantidadeDe}", que não existe antes do grupo.`)
      else if (alvo.tipo !== 'NUMERO') problemas.push(`${onde}: a quantidade precisa vir de um campo Número.`)
    }
    const chavesGrupo = new Set<string>()
    campos.forEach((sub, j) => {
      const ondeSub = `${onde}, subcampo ${j + 1}${sub.rotulo ? ` ("${sub.rotulo}")` : ''}`
      if (sub.tipo === 'GRUPO') problemas.push(`${ondeSub}: grupo dentro de grupo não é permitido.`)
      if (sub.chave && chavesGrupo.has(sub.chave)) problemas.push(`${ondeSub}: chave "${sub.chave}" repetida no grupo.`)
      if (sub.chave) chavesGrupo.add(sub.chave)
      problemas.push(...problemasDoCampo(sub, etapa, ondeSub, camposReferenciaveis(schema.itens, i, j)))
    })
    ;(item.validacoes ?? []).forEach((v) => {
      const alvo = campos.find((c) => c.chave === v.campo)
      if (!alvo) problemas.push(`${onde}: a soma usa um subcampo que não existe.`)
      else if (!ehNumerico(alvo.tipo)) problemas.push(`${onde}: a soma precisa ser de um subcampo numérico.`)
      if (etapa === 'ESTRUTURACAO' && v.marco === 'ENVIO_ANALISE') {
        problemas.push(`${onde}: na Estruturação a soma não pode valer no envio para análise.`)
      }
    })
  })
  chavesTopo.forEach((n, chave) => {
    if (n > 1) problemas.push(`A chave "${chave}" está repetida.`)
  })
  return problemas
}

const semVazios = <T extends object>(o: T): T =>
  Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ) as T

/**
 * O que vai ao backend: tira o que não pertence ao tipo (opções fora de OPCAO, campos fora
 * de GRUPO…), para que trocar o tipo de um campo não deixe lixo gravado na versão imutável.
 */
export const normalizarSchema = (schema: SchemaFormulario): SchemaFormulario => {
  const item = (i: ItemFormulario, dentroDeGrupo: boolean): ItemFormulario => {
    const base: ItemFormulario = semVazios({
      chave: i.chave.trim(),
      rotulo: i.rotulo.trim(),
      tipo: i.tipo,
      obrigatorioEm: i.obrigatorioEm,
      condition: normalizarCondicao(i.condition),
      requiredWhen: normalizarCondicao(i.requiredWhen),
    })
    if (i.tipo !== 'GRUPO' && i.tipo !== 'CPF' && i.enviarParaIa) base.enviarParaIa = true
    if (i.tipo === 'OPCAO') base.opcoes = (i.opcoes ?? []).map((o) => ({ valor: o.valor.trim(), rotulo: o.rotulo.trim() }))
    if (ehNumerico(i.tipo)) {
      if (i.min != null) base.min = i.min
      if (i.max != null) base.max = i.max
    }
    if (i.tipo === 'GRUPO' && !dentroDeGrupo) {
      base.repeatableGroup = !!i.repeatableGroup
      if (i.repeatableGroup) {
        if (i.quantidadeDe) base.quantidadeDe = i.quantidadeDe
        if (i.minItems != null) base.minItems = i.minItems
        if (i.maxItems != null) base.maxItems = i.maxItems
        if (i.validacoes?.length) base.validacoes = i.validacoes.map((v) => semVazios(v))
      }
      base.campos = (i.campos ?? []).map((c) => item(c, true))
    }
    return base
  }
  return { itens: schema.itens.map((i) => item(i, false)) }
}

export const schemaVazio = (): SchemaFormulario => ({ itens: [] })

/** Comparação estável para saber se há alteração não salva. */
export const mesmoSchema = (a: SchemaFormulario, b: SchemaFormulario) =>
  JSON.stringify(normalizarSchema(a)) === JSON.stringify(normalizarSchema(b))
